import React, { useEffect, useState } from 'react';
import { useApp } from '@/context/AppContext';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Download, Share, PlusSquare } from 'lucide-react';
import { canInstall, subscribeInstall, promptInstall, isStandalone, isIOS } from '@/lib/pwa';

// Prompts the user to install the app to their home screen / desktop after login.
export function InstallPrompt() {
  const { user, booting } = useApp();
  const [ready, setReady] = useState(canInstall());
  const [open, setOpen] = useState(false);
  const [iosMode, setIosMode] = useState(false);

  useEffect(() => {
    // Register the service worker so the app is installable (and push works).
    let updTimer;
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('/sw.js').then((reg) => {
        // Periodically ask the browser to re-check for a newer service worker.
        // The <UpdateBanner /> handles surfacing the "refresh" prompt.
        updTimer = setInterval(() => { reg.update().catch(() => {}); }, 60 * 60 * 1000);
      }).catch(() => {});
    }
    const unsub = subscribeInstall(() => setReady(canInstall()));
    const onInstalled = () => { setOpen(false); try { sessionStorage.setItem('sc_install_done', '1'); } catch (e) {} };
    window.addEventListener('appinstalled', onInstalled);
    return () => { unsub(); window.removeEventListener('appinstalled', onInstalled); if (updTimer) clearInterval(updTimer); };
  }, []);

  useEffect(() => {
    if (booting || !user) return;
    if (isStandalone()) return;
    if (sessionStorage.getItem('sc_install_dismissed') || sessionStorage.getItem('sc_install_done')) return;
    const t = setTimeout(() => {
      if (canInstall()) { setIosMode(false); setOpen(true); }
      else if (isIOS()) { setIosMode(true); setOpen(true); }
    }, 1500);
    return () => clearTimeout(t);
  }, [booting, user, ready]);

  const dismiss = () => { try { sessionStorage.setItem('sc_install_dismissed', '1'); } catch (e) {} setOpen(false); };

  const install = async () => {
    await promptInstall();
    setOpen(false);
    try { sessionStorage.setItem('sc_install_done', '1'); } catch (e) {}
  };

  if (!open) return null;

  return (
    <AlertDialog open={open} onOpenChange={(v) => { if (!v) dismiss(); }}>
      <AlertDialogContent data-testid="install-prompt">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary font-display text-base font-bold text-primary-foreground">S</span>
            Install SHABZ CLOSET
          </AlertDialogTitle>
          <AlertDialogDescription>
            {iosMode
              ? 'Add SHABZ CLOSET to your Home Screen for a full-screen app with its own icon.'
              : 'Install the app for one-tap access with its own icon — no browser bar, works like a native app.'}
          </AlertDialogDescription>
        </AlertDialogHeader>
        {iosMode && (
          <div className="space-y-2 rounded-xl border border-border bg-secondary/40 p-3 text-sm">
            <p className="flex items-center gap-2"><Share className="h-4 w-4 text-primary" /> 1. Tap the <b>Share</b> button in Safari</p>
            <p className="flex items-center gap-2"><PlusSquare className="h-4 w-4 text-primary" /> 2. Choose <b>Add to Home Screen</b></p>
          </div>
        )}
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="install-later">Maybe later</AlertDialogCancel>
          {!iosMode && (
            <AlertDialogAction onClick={install} data-testid="install-now">
              <Download className="mr-1 h-4 w-4" /> Install
            </AlertDialogAction>
          )}
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
